/**
 * **Pausing, deleting and restoring a catalogue row** — one place for all
 * three tables (C25, Wave 27D, T-1.31; ADR-0054 §1, §3; waves.md Wave 27 I-5).
 *
 *     const moves = useCatalogueMoves({ onMoved: load })
 *     <MoveButtons target={{ kind: 'service', id, name, status }} moves={moves} />
 *     {moves.dialog}
 *
 * The words the Branches screen set for a branch are the words here:
 * *Deactivate* and *Activate* move a row between active and inactive;
 * **Delete** asks first through `ConfirmDialog` and names what still uses the
 * row (`GET …/usage`) — open cases, booking flows, website sections, and a
 * category's services; **Restore** asks too, because a restored row comes back
 * **inactive**, by the owner's choice: an old price must not go live on the
 * website or the booking page until someone has looked at it.
 *
 * A category that still holds services cannot be deleted (ADR-0054 §3). Its
 * dialog says how many and where to move them, and offers no *Delete* at all,
 * rather than a button the service would refuse.
 */

import { Button, ConfirmDialog, Dialog, buttonClass } from '@fiveox/ui'
import { Pencil, Power, RotateCcw, Trash2 } from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import { Link } from 'react-router'

import { DialogActions } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type CatalogueUsage, type Lifecycle, nextStatuses, services } from '../../lib/api/services'

export type CatalogueKind = 'category' | 'service' | 'plan'
export type MoveAction = 'deactivate' | 'activate' | 'delete' | 'restore'

export interface MoveTarget {
  kind: CatalogueKind
  id: string
  name: string
  status: Lifecycle
}

/** The state each action lands in — I-5's moves, and a restore lands inactive. */
export const LANDS_IN: Record<MoveAction, Lifecycle> = {
  deactivate: 'inactive',
  activate: 'active',
  delete: 'deleted',
  restore: 'inactive',
}

const NOUN: Record<CatalogueKind, string> = { category: 'category', service: 'service', plan: 'plan' }

/** What a row in this state may do, in the order the buttons are drawn —
 *  read off `nextStatuses`, so the screen and the client's rule are one. */
export function actionsFor(status: Lifecycle): MoveAction[] {
  return nextStatuses(status).map((to) =>
    to === 'deleted' ? 'delete' : status === 'deleted' ? 'restore' : to === 'active' ? 'activate' : 'deactivate',
  )
}

/** `2 open cases`, `1 booking flow` — what still names a row, in words. A
 *  category's own services are said separately, because they refuse its delete. */
export function usageLines(usage: CatalogueUsage): string[] {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  const lines: string[] = []
  if (usage.open_cases) lines.push(plural(usage.open_cases, 'open case', 'open cases'))
  if (usage.booking_flows) lines.push(plural(usage.booking_flows, 'booking flow', 'booking flows'))
  if (usage.site_sections) lines.push(plural(usage.site_sections, 'website section', 'website sections'))
  return lines
}

async function move(target: MoveTarget, to: Lifecycle): Promise<void> {
  if (target.kind === 'category') await services.setCategoryStatus(target.id, to)
  else if (target.kind === 'plan') await services.setPlanStatus(target.id, to)
  else await services.setStatus(target.id, to)
}

async function usageOf(target: MoveTarget): Promise<CatalogueUsage> {
  if (target.kind === 'category') return (await services.categoryUsage(target.id)).data
  if (target.kind === 'plan') return (await services.planUsage(target.id)).data
  return (await services.usage(target.id)).data
}

/** After the move, in the firm's words. */
function doneSentence(target: MoveTarget, action: MoveAction): string {
  const name = `“${target.name}”`
  switch (action) {
    case 'deactivate':
      return `${name} is inactive. It is offered nowhere new until you activate it.`
    case 'activate':
      return `${name} is active again.`
    case 'delete':
      return `${name} is deleted. You will find it under Deleted, and can restore it there.`
    case 'restore':
      return `${name} is restored, and inactive. Activate it when you have checked it.`
  }
}

/** What pausing does, for each kind — and what it does not. */
function pauseSentence(target: MoveTarget): string {
  const name = `“${target.name}”`
  if (target.kind === 'category') {
    return `Every service in ${name} stops being offered while it is inactive — on your website, the booking page, the portal and a new case. Cases and bookings already running carry on, and you can activate it again at any time.`
  }
  if (target.kind === 'plan') {
    return `${name} stops being offered on your website, the booking page and a new case. Cases already on it carry on, you can still invoice it by hand, and you can activate it again at any time.`
  }
  return `${name} stops being offered — on your website, the booking page, the portal, a new case, an import and a calendar run. Cases and bookings already running carry on, and you can activate it again at any time.`
}

type Usage = CatalogueUsage | 'loading' | 'failed' | null

export interface CatalogueMoves {
  /** Ask for a move: *Activate* happens at once; the others ask first. */
  request: (target: MoveTarget, action: MoveAction) => void
  /** The row being moved, while its request is in flight. */
  busyId: string | null
  /** Render once, anywhere on the page. */
  dialog: ReactNode
}

export function useCatalogueMoves({
  onMoved,
  onError,
}: {
  /** Reload, then say what happened. */
  onMoved: (sentence: string) => Promise<void> | void
  /** A refusal from a move that asked nothing first (*Activate*). */
  onError: (sentence: string) => void
}): CatalogueMoves {
  const [pending, setPending] = useState<{ target: MoveTarget; action: MoveAction } | null>(null)
  const [usage, setUsage] = useState<Usage>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const close = useCallback(() => {
    setPending(null)
    setUsage(null)
    setError(null)
  }, [])

  const run = useCallback(
    async (target: MoveTarget, action: MoveAction, inDialog: boolean) => {
      setBusyId(target.id)
      setError(null)
      try {
        await move(target, LANDS_IN[action])
      } catch (err) {
        const sentence = err instanceof ApiError ? err.message : 'That could not be done.'
        if (inDialog) setError(sentence)
        else onError(sentence)
        setBusyId(null)
        return
      }
      if (inDialog) close()
      setBusyId(null)
      try {
        await onMoved(doneSentence(target, action))
      } catch {
        // The move happened; only the reload after it failed.
        onError('That was done, but the list could not be reloaded. Refresh the page to see it.')
      }
    },
    [close, onMoved, onError],
  )

  const request = useCallback(
    (target: MoveTarget, action: MoveAction) => {
      if (action === 'activate') {
        void run(target, action, false)
        return
      }
      setError(null)
      setPending({ target, action })
      if (action === 'delete') {
        setUsage('loading')
        usageOf(target)
          .then(setUsage)
          .catch(() => setUsage('failed'))
      } else {
        setUsage(null)
      }
    },
    [run],
  )

  let dialog: ReactNode = null
  if (pending) {
    const { target, action } = pending
    const name = `“${target.name}”`
    const noun = NOUN[target.kind]
    const confirm = () => void run(target, action, true)
    const busy = busyId === target.id

    if (action === 'delete' && target.kind === 'category' && typeof usage === 'object' && usage && (usage.services ?? 0) > 0) {
      const held = usage.services ?? 0
      dialog = (
        <Dialog open onClose={close} title={`${name} still holds services`}>
          <p className="text-muted-foreground text-sm" data-move-held="">
            {name} still holds {held} {held === 1 ? 'service' : 'services'}. Move{' '}
            {held === 1 ? 'it' : 'them'} to another category on the service’s own page, or delete{' '}
            {held === 1 ? 'it' : 'them'}, first. A category is deleted only when nothing is left in it.
          </p>
          <DialogActions>
            <Button variant="outline" onClick={close}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (action === 'delete') {
      const lines = typeof usage === 'object' && usage ? usageLines(usage) : []
      dialog = (
        <ConfirmDialog
          open
          onClose={close}
          title={`Delete ${name}?`}
          confirmLabel={`Delete ${noun}`}
          cancelLabel={`Keep the ${noun}`}
          tone="danger"
          busy={busy || usage === 'loading'}
          busyLabel={usage === 'loading' ? 'Checking what uses it…' : 'Deleting…'}
          error={error}
          onConfirm={confirm}
        >
          <p>
            It goes from every list but <strong>Deleted</strong>. Nothing is erased: the cases, invoices and bookings
            that name it keep it, and you can restore it later — it comes back inactive.
          </p>
          {usage === 'failed' && <p data-move-usage="">What still uses it could not be checked.</p>}
          {typeof usage === 'object' && usage && (
            <p data-move-usage="">
              {lines.length > 0 ? (
                <>
                  Still using it: <strong className="text-foreground">{lines.join(' · ')}</strong>. They keep working;
                  it is not offered for anything new.
                </>
              ) : (
                'Nothing open uses it.'
              )}
            </p>
          )}
        </ConfirmDialog>
      )
    } else if (action === 'restore') {
      dialog = (
        <ConfirmDialog
          open
          onClose={close}
          title={`Restore ${name}?`}
          confirmLabel="Restore"
          busy={busy}
          error={error}
          onConfirm={confirm}
        >
          <p data-move-restore="">
            It comes back <strong className="text-foreground">inactive</strong> — not on your website, the booking
            page or a new case until you activate it — so you can check it first.
          </p>
        </ConfirmDialog>
      )
    } else {
      dialog = (
        <ConfirmDialog
          open
          onClose={close}
          title={`Deactivate ${name}?`}
          confirmLabel="Deactivate"
          cancelLabel="Leave it active"
          busy={busy}
          error={error}
          onConfirm={confirm}
        >
          <p>{pauseSentence(target)}</p>
        </ConfirmDialog>
      )
    }
  }

  return { request, busyId, dialog }
}

const ICON: Record<MoveAction, ReactNode> = {
  deactivate: <Power size={14} aria-hidden />,
  activate: <Power size={14} aria-hidden />,
  delete: <Trash2 size={14} aria-hidden />,
  restore: <RotateCcw size={14} aria-hidden />,
}

const LABEL: Record<MoveAction, string> = {
  deactivate: 'Deactivate',
  activate: 'Activate',
  delete: 'Delete',
  restore: 'Restore',
}

/**
 * A row's buttons: *Edit* (a link to the record, or a dialog), then the moves
 * its state allows. Clicks stop here, so a button in a row that opens a record
 * does not open the record too.
 */
export function MoveButtons({
  target,
  moves,
  edit,
  compact = false,
}: {
  target: MoveTarget
  moves: CatalogueMoves
  /** A link to the record, or a function that opens an edit dialog. */
  edit?: string | (() => void)
  /** Icons only below `sm`, for a table on a phone. */
  compact?: boolean
}) {
  const text = (label: string) => (compact ? <span className="sr-only sm:not-sr-only">{label}</span> : label)
  const aria = (label: string) => `${label} ${target.name}`
  return (
    <div
      className="flex flex-wrap items-center justify-end gap-1"
      data-move-buttons={target.id}
      onClick={(event) => event.stopPropagation()}
    >
      {edit && target.status !== 'deleted' && typeof edit === 'string' && (
        <Link
          to={edit}
          aria-label={aria('Edit')}
          className={buttonClass({ variant: 'ghost', size: 'sm' })}
        >
          <Pencil size={14} aria-hidden /> {text('Edit')}
        </Link>
      )}
      {edit && target.status !== 'deleted' && typeof edit === 'function' && (
        <Button size="sm" variant="ghost" aria-label={aria('Edit')} onClick={edit}>
          <Pencil size={14} aria-hidden /> {text('Edit')}
        </Button>
      )}
      {actionsFor(target.status).map((action) => (
        <Button
          key={action}
          size="sm"
          variant="ghost"
          aria-label={aria(LABEL[action])}
          disabled={moves.busyId === target.id}
          onClick={() => moves.request(target, action)}
        >
          {ICON[action]} {text(LABEL[action])}
        </Button>
      ))}
    </div>
  )
}
